import { useRef } from "react";

import * as Sentry from "@sentry/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import CheckIcon from "@uui/CheckCircle";
import XIcon from "@uui/XCircle";

import LoadingButton from "@mui/lab/LoadingButton";
import { Collapse, Typography } from "@mui/material";

import * as Events from "@app/analytics/tracking/events";
import api from "@app/common/access/ShareReport/api";
import { getAccessQueryKey } from "@app/common/access/ShareReport/hooks/useReportShare";
import ROUTES from "@app/common/routes";

import { Link } from "@rui/Link";
import ReadLogo from "@rui/ReadLogo";
import Stack, { Row } from "@rui/Stack";
import TextField from "@rui/TextField";

const AccessRequested = ({ message }: { message: string }) => {
  return (
    <Row spacing={1} variant="left">
      <CheckIcon
        sx={{ color: "shades.meadow.600", height: "16px", width: "16px" }}
      />
      <Typography sx={{ color: "shades.meadow.600" }}>{message}</Typography>
    </Row>
  );
};

const AccessFailed = () => {
  return (
    <Row spacing={1} variant="left">
      <XIcon
        sx={{ color: "shades.negative.600", height: "16px", width: "16px" }}
      />
      <Typography sx={{ color: "shades.negative.600" }}>
        Request failed, please try again
      </Typography>
    </Row>
  );
};

interface MeetingReportProps {
  reportType: "meeting";
}

interface TopicReportProps {
  reportType: "topic";
  requesterId: string;
  ownerId: string;
}

type ReportRequestAccessProps = {
  reportId: string;
} & (MeetingReportProps | TopicReportProps);

const ReportRequestAccess = (props: ReportRequestAccessProps) => {
  const { reportId, reportType } = props;

  const queryClient = useQueryClient();
  const requestAccessMutation = useMutation({
    mutationFn: api.access.requestAccess,
    onError: (error: any) => {
      Sentry.addBreadcrumb({
        message: "Request access failed",
        level: "error",
        data: { reportId, reportType },
      });
      Sentry.captureException(error);
    },
  });

  const textRef = useRef<HTMLInputElement>(null);

  const handleRequestAccess = () => {
    Events.requestAccess({ reportType });

    const message = textRef.current?.value ?? "";
    const mutationParams =
      reportType === "topic"
        ? {
            reportId,
            reportType,
            requesterId: props.requesterId,
            ownerId: props.ownerId,
            message,
          }
        : { reportId, reportType, message };

    requestAccessMutation.mutate(mutationParams, {
      onSettled: () => {
        if (reportType === "meeting") {
          queryClient.invalidateQueries({
            queryKey: getAccessQueryKey(reportId),
          });
        }
      },
    });
  };

  return (
    <Stack variant="center" flex={1} spacing={3} sx={{ bgcolor: "#FFF" }}>
      <ReadLogo />
      <Stack variant="center" spacing={2} sx={{ maxWidth: "520px" }}>
        <Typography variant="h1" sx={{ fontSize: "2rem" }}>
          You need access
        </Typography>
        <Typography sx={{ textAlign: "center" }}>
          To view this {reportType === "topic" ? "Readout" : "meeting report"},
          you can request access from the report owner.
        </Typography>
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{ width: "100%" }}
        >
          <>
            <Collapse in={requestAccessMutation.isError}>
              <AccessFailed />
            </Collapse>
            <Collapse in={requestAccessMutation.isSuccess}>
              <AccessRequested
                message={
                  requestAccessMutation.data?.status === "hasAccess"
                    ? "Access already granted"
                    : "Access requested"
                }
              />
            </Collapse>
            <Collapse
              in={!requestAccessMutation.isSuccess}
              sx={{ width: "100%" }}
            >
              <Stack alignItems="center" sx={{ width: "100%" }} spacing={2}>
                <TextField
                  placeholder="Add a message (optional)"
                  multiline
                  minRows={3}
                  ref={textRef}
                  sx={{ width: "100%", p: 0 }}
                  InputProps={{ sx: { p: 0 } }}
                />
                <LoadingButton
                  loading={requestAccessMutation.isPending}
                  variant="contained"
                  onClick={handleRequestAccess}
                  sx={{ height: "42px", width: "164px" }}
                >
                  Request Access
                </LoadingButton>
              </Stack>
            </Collapse>
          </>
        </Stack>
        <Link
          variant="bodySecondaryMed"
          color="shades.primary.500"
          to={ROUTES.MEETING_REPORTS}
        >
          Back to Reports
        </Link>
      </Stack>
    </Stack>
  );
};

export default ReportRequestAccess;
